import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
// App axios instance and endpoints
import { API_BASE_URL, axiosInstance, API_ENDPOINTS } from "config/api";
// Bill component and helpers for PDF/upload
import Bill, {
  generateBillPdf,
  uploadFile,
} from "views/admin/default/components/Bill";
import { renderAndUploadBill } from "views/admin/default/utils/billPdfHelper";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Avatar,
  Flex,
  FormLabel,
  Select,
  Input,
  Button,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
  Divider,
  Textarea,
  Stack,
  Badge,
} from "components-lib";
import FileUploadField from "../../../components/FileUploadField";

// --------- Local helpers and constants (resolve ESLint undefineds) ---------
const PaymentStates = {
  PAID: "PAID",
  PENDING: "PENDING",
};

const backendBase = API_BASE_URL.replace(/\/+$/g, "");

const INITIAL_BILL_SUMMARY = {
  grossAmount: 0,
  subtotal: 0,
  discountAmount: 0,
  emergencyCharge: 0,
  outsideOPDEmergencyCharge: 0,
  taxAmount: 0,
  netAmount: 0,
  RecivedAmt: 0,
  pendingAmount: 0,
  refundAmount: 0,
};

const normalizeNumber = (value) => {
  const n = parseFloat(value);
  return Number.isNaN(n) ? 0 : n;
};

const ensureArray = (files) => (files ? Array.from(files) : []);

const convertTo12HourFormat = (time24) => {
  if (!time24) return "";
  try {
    const parts = String(time24).split(":");
    const hStr = parts[0];
    const mStr = parts[1] ?? "00";
    let h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return String(time24);
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
  } catch {
    return String(time24);
  }
};

const convertTo24HourFormat = (time12) => {
  if (!time12) return "";
  const s = String(time12).trim();
  const match = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match) {
    let h = parseInt(match[1], 10);
    const min = match[2];
    const ampm = match[3].toUpperCase();
    if (ampm === "AM") {
      if (h === 12) h = 0;
    } else {
      if (h !== 12) h += 12;
    }
    return `${String(h).padStart(2, "0")}:${min}:00`;
  }
  // Already looks like 24h (HH:MM or HH:MM:SS)
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) {
    return s.length === 5 ? `${s}:00` : s;
  }
  return s;
};

const BILLING_UPDATE_URL = (id) => `${backendBase}/api/bills/update/${id}`;

const EditAppointmentModal = ({ isOpen, onClose, appointment, onUpdate }) => {
  const toast = useToast();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef();

  const [appointmentData, setAppointmentData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [doctorServices, setDoctorServices] = useState([]);
  const [serviceDetails, setServiceDetails] = useState({
    serviceId: "",
    unitPrice: "",
    discount: "",
  });
  const [services, setServices] = useState([]);
  const [computedServices, setComputedServices] = useState([]);
  const [isEmergency, setIsEmergency] = useState(0);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [lastFetchedDoctorId, setLastFetchedDoctorId] = useState(null);
  const [selectedDoctorDetails, setSelectedDoctorDetails] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState(null);

  const [existingBillData, setExistingBillData] = useState(null);
  const [billSummary, setBillSummary] = useState(INITIAL_BILL_SUMMARY);
  const [billSummaryVisible, setBillSummaryVisible] = useState(false);
  const [paymentState, setPaymentState] = useState(PaymentStates.PENDING);
  const [billPaymentNowSelection, setBillPaymentNowSelection] = useState(null);
  const [billDescription, setBillDescription] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("Pending");
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [receiptError, setReceiptError] = useState("");
  const [isBillProcessing, setIsBillProcessing] = useState(false);

  const appointmentTypes = useMemo(
    () => [
      { value: "in_clinic", label: "In Clinic" },
      { value: "online", label: "Online" },
    ],
    [],
  );

  // Show existing bill's received amount explicitly in the UI
  const existingBillReceived = useMemo(() => {
    if (!existingBillData) return 0;
    return normalizeNumber(
      existingBillData.received_amt ?? existingBillData.RecivedAmt ?? 0,
    );
  }, [existingBillData]);

  const paymentBadgeScheme = useMemo(() => {
    switch (paymentState) {
      case PaymentStates.PAID:
        return "green";
      case PaymentStates.PENDING:
        return "orange";
      default:
        return "red";
    }
  }, [paymentState]);

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const calculateBillSummary = useCallback(
    (options = {}) => {
      const sourceBill = options.existingBill || existingBillData;
      const doctorInfo =
        (selectedDoctorDetails &&
          (selectedDoctorDetails.doctor || selectedDoctorDetails)) ||
        {};

      const emergencyChargeValue = normalizeNumber(
        doctorInfo.emergency_charge ||
          doctorInfo.emergencyCharge ||
          sourceBill?.emergency_charge ||
          sourceBill?.emergencyCharge ||
          sourceBill?.emergency_fee ||
          0,
      );

      const outsideChargeValue = normalizeNumber(
        doctorInfo.outside_opd_emergency_charge ||
          doctorInfo.outside_opd_charge ||
          doctorInfo.outsideOpdCharge ||
          sourceBill?.outside_opd_emergency_charge ||
          sourceBill?.outside_opd_charge ||
          sourceBill?.outsideCharge ||
          0,
      );

      const baseServices = services.map((service) => ({
        serviceName: service.serviceName || service.name || "Service",
        unitPrice: normalizeNumber(service.unitPrice),
        discount: normalizeNumber(service.discount),
      }));

      const servicesForBill = baseServices.filter((service) => {
        const name = (service.serviceName || "").toLowerCase();
        if (name === "emergency charge" && Number(isEmergency) !== 1) {
          return false;
        }
        if (
          name === "outside opd emergency charge" &&
          Number(appointmentData?.isOutsideOPDEmergency) !== 1
        ) {
          return false;
        }
        return true;
      });

      let grossAmount = 0;
      let discountTotal = 0;

      servicesForBill.forEach((service) => {
        grossAmount += service.unitPrice;
        discountTotal += (service.unitPrice * service.discount) / 100;
      });

      const emergencyChargePresentIndex = servicesForBill.findIndex(
        (service) => service.serviceName?.toLowerCase() === "emergency charge",
      );

      if (Number(isEmergency) === 1) {
        if (emergencyChargePresentIndex === -1 && emergencyChargeValue > 0) {
          servicesForBill.push({
            serviceName: "Emergency Charge",
            unitPrice: emergencyChargeValue,
            discount: 0,
          });
          grossAmount += emergencyChargeValue;
        } else if (emergencyChargePresentIndex !== -1) {
          grossAmount += normalizeNumber(
            servicesForBill[emergencyChargePresentIndex].unitPrice,
          );
        }
      }

      const outsideChargeIndex = servicesForBill.findIndex(
        (service) =>
          service.serviceName?.toLowerCase() === "outside opd emergency charge",
      );

      if (Number(appointmentData?.isOutsideOPDEmergency) === 1) {
        if (outsideChargeIndex === -1 && outsideChargeValue > 0) {
          servicesForBill.push({
            serviceName: "Outside OPD Emergency Charge",
            unitPrice: outsideChargeValue,
            discount: 0,
          });
          grossAmount += outsideChargeValue;
        } else if (outsideChargeIndex !== -1) {
          grossAmount += normalizeNumber(
            servicesForBill[outsideChargeIndex].unitPrice,
          );
        }
      }

      const subtotal = grossAmount - discountTotal;
      // Use doctor's tax percentage if provided; default to 10%
      const taxPercent = Number(
        options.existingBill?.tax_percentage ??
        options.existingBill?.taxPercentage ??
        selectedDoctorDetails?.doctor?.tax_percentage ??
        selectedDoctorDetails?.doctor?.taxPercentage ??
        selectedDoctorDetails?.tax_percentage ??
        selectedDoctorDetails?.taxPercentage ??
        10,
      );
      const taxAmount = subtotal * (taxPercent / 100);
      const netAmount = subtotal + taxAmount;

      // Always use existing bill's received amount if it exists
      const existingRec = normalizeNumber(
        options.existingBill?.RecivedAmt ||
          options.existingBill?.received_amt ||
          sourceBill?.RecivedAmt ||
          sourceBill?.received_amt ||
          0,
      );

      let safeReceivedAmount;
      if (existingRec > 0) {
        // If existing bill has received amount, always use it (even if > netAmount)
        safeReceivedAmount = existingRec;
      } else {
        // Default to netAmount if no existing received amount
        safeReceivedAmount = netAmount;
      }

      let pendingAmount = Number((netAmount - safeReceivedAmount).toFixed(2));
      const refundAmount = pendingAmount < 0 ? Math.abs(pendingAmount) : 0;

      const summary = {
        grossAmount: Number(grossAmount.toFixed(2)),
        subtotal: Number(subtotal.toFixed(2)),
        discountAmount: Number(Math.abs(discountTotal).toFixed(2)),
        emergencyCharge: Number(
          (Number(isEmergency) === 1 ? emergencyChargeValue : 0).toFixed(2),
        ),
        outsideOPDEmergencyCharge: Number(
          (Number(appointmentData?.isOutsideOPDEmergency) === 1
            ? outsideChargeValue
            : 0
          ).toFixed(2),
        ),
        taxAmount: Number(taxAmount.toFixed(2)),
        taxPercentage: Number(taxPercent),
        netAmount: Number(netAmount.toFixed(2)),
        RecivedAmt: Number(safeReceivedAmount.toFixed(2)),
        pendingAmount: Number(pendingAmount.toFixed(2)),
        refundAmount: Number(refundAmount.toFixed(2)),
      };

      setComputedServices(servicesForBill);
      setBillSummary(summary);

      const nextInvoiceStatus =
        summary.refundAmount > 0
          ? "Refund"
          : summary.pendingAmount > 0
            ? "Pending"
            : summary.RecivedAmt > 0
              ? "Paid"
              : "Booked";
      setInvoiceStatus(nextInvoiceStatus);

      // Only PAID or PENDING states
      // If pendingAmount is not zero (positive or negative), mark as PENDING
      // Only if pendingAmount is exactly 0, mark as PAID
      const nextPaymentState =
        summary.pendingAmount !== 0
          ? PaymentStates.PENDING
          : PaymentStates.PAID;
      setPaymentState(nextPaymentState);

      if (nextPaymentState === PaymentStates.PAID) {
        setBillPaymentNowSelection("yes");
      } else {
        setBillPaymentNowSelection("no");
      }

      return summary;
    },
    [
      services,
      isEmergency,
      appointmentData?.isOutsideOPDEmergency,
      selectedDoctorDetails,
      existingBillData,
    ],
  );

  const fetchExistingBill = useCallback(
    async (appointmentId) => {
      if (!appointmentId) return null;
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("Token");
        const response = await axiosInstance.get("api/bills/getAll", {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
          params: { appointment_id: appointmentId },
        });
        const bills =
          response?.data?.bills || response?.data?.data || response?.data || [];
        const billRecord = Array.isArray(bills) ? bills[0] : bills;
        if (!billRecord) {
          setExistingBillData(null);
          return null;
        }
        setExistingBillData(billRecord);
        setBillDescription(
          billRecord.bill_description || billRecord.billDescription || "",
        );
        calculateBillSummary({ existingBill: billRecord });
        return billRecord;
      } catch (error) {
        console.error("Failed to fetch bill for appointment", error);
        setExistingBillData(null);
        return null;
      }
    },
    [calculateBillSummary],
  );

  const fetchDoctorDetails = useCallback(
    async (doctorId, showToast = true) => {
      if (!doctorId) return null;
      if (lastFetchedDoctorId === doctorId && doctorServices.length > 0) {
        return selectedDoctorDetails;
      }
      try {
        setIsLoadingServices(true);
        const response = await axiosInstance.get(
          `api/doctor/profile?doctor_id=${doctorId}`,
        );
        const doctorData = response.data?.doctor || response.data;
        const serviceNames = doctorData?.service
          ? doctorData.service.split(",").map((s) => s.trim())
          : [];
        const unitPrices = doctorData?.unit_price
          ? doctorData.unit_price.split(",").map((p) => p.trim())
          : [];
        const discounts = doctorData?.discount
          ? doctorData.discount.split(",").map((d) => d.trim())
          : [];

        const formattedServices = serviceNames.map((serviceName, index) => ({
          id: index + 1,
          name: serviceName,
          unitPrice: unitPrices[index] || "0",
          discount: discounts[index] || "0",
        }));

        setDoctorServices(formattedServices);
        setLastFetchedDoctorId(doctorId);
        setSelectedDoctorDetails(response.data);

        if (showToast) {
          toast({
            title: "Services Loaded",
            description: `${formattedServices.length} service(s) available for this doctor`,
            status: formattedServices.length ? "success" : "info",
            duration: 2500,
            isClosable: true,
          });
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching doctor details", error);
        setDoctorServices([]);
        setSelectedDoctorDetails(null);
        if (showToast) {
          toast({
            title: "Error",
            description:
              error.response?.data?.message ||
              "Failed to load doctor services. Please try again.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
        return null;
      } finally {
        setIsLoadingServices(false);
      }
    },
    [doctorServices.length, lastFetchedDoctorId, selectedDoctorDetails, toast],
  );

  const fetchTimeSlots = useCallback(
    async (doctorId, selectedDate) => {
      if (!doctorId || !selectedDate) {
        setAvailableTimeSlots([]);
        return;
      }
      try {
        setIsLoadingTimeSlots(true);
        const response = await axiosInstance.get("api/doctor/getSlots", {
          params: { id: doctorId, date: selectedDate },
        });
        const slotsData = response?.data?.slots || response?.data || [];
        const currentDate = new Date();
        const selectedDateObj = new Date(selectedDate);
        const isToday =
          selectedDateObj.toDateString() === currentDate.toDateString();

        const slotDetails = slotsData.map((timeString) => {
          const [hours, minutes] = timeString.split(":").map(Number);
          const ampm = hours >= 12 ? "PM" : "AM";
          const displayHours =
            hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
          const display = `${String(displayHours).padStart(2, "0")}:${String(
            minutes,
          ).padStart(2, "0")} ${ampm}`;

          let isPast = false;
          if (isToday) {
            const slotTime = new Date();
            slotTime.setHours(hours, minutes, 0, 0);
            isPast = slotTime < currentDate;
          }

          return {
            value: timeString,
            display,
            isPast,
          };
        });
        setAvailableTimeSlots(slotDetails);
        if (slotDetails.length === 0) {
          toast({
            title: "No Slots",
            description:
              "No appointment slots available for the selected date.",
            status: "warning",
            duration: 2500,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching time slots", error);
        setAvailableTimeSlots([]);
        toast({
          title: "Error",
          description:
            error.response?.data?.message ||
            "Failed to fetch available time slots.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoadingTimeSlots(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const response = await axiosInstance.get(API_ENDPOINTS.DOCTOR.ALL);
        if (Array.isArray(response.data)) {
          setDoctors(response.data);
        } else if (Array.isArray(response.data?.data)) {
          setDoctors(response.data.data);
        } else {
          setDoctors([]);
        }
      } catch (error) {
        console.error("Failed to load doctors", error);
        setDoctors([]);
      }
    };
    fetchDoctors();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setServiceDetails({ serviceId: "", unitPrice: "", discount: "" });
      setDoctorServices([]);
      setLastFetchedDoctorId(null);
      setAvailableTimeSlots([]);
      setAppointmentData(null);
      setOriginalData(null);
      setServices([]);
      setComputedServices([]);
      setExistingBillData(null);
      setBillSummary(INITIAL_BILL_SUMMARY);
      setBillSummaryVisible(false);
      setReceiptFiles([]);
      setReceiptError("");
    }
  }, [isOpen]);

  useEffect(() => {
    const loadAppointmentDetails = async () => {
      if (!isOpen || !appointment?.id) return;
      setIsLoading(true);
      try {
        const response = await axiosInstance.get(
          `/api/appointment/getAppointmentById?id=${appointment.id}`,
        );
        if (response.data?.success && response.data.data) {
          const details = response.data.data;
          const servicesList = [];
          if (details.service) {
            const serviceNames = details.service.split(",");
            const unitPrices = details.unit_price
              ? details.unit_price.split(",").map(Number)
              : [];
            const discounts = details.discount
              ? details.discount.split(",").map(Number)
              : [];
            serviceNames.forEach((name, index) => {
              servicesList.push({
                serviceName: name.trim(),
                unitPrice: unitPrices[index] || 0,
                discount: discounts[index] || 0,
              });
            });
          }
          const formattedTime = convertTo12HourFormat(details.appointment_time);
          const formattedData = {
            ...details,
            appointmentDate: details.appointment_date,
            appointmentTime: formattedTime || details.appointment_time,
            appointmentType: details.appointment_type,
            doctorId: details.doctor_id,
            patientId: details.patient_id,
            patientName: details.patient_name || details.patientName,
            patientGender: details.patient_gender || details.patientGender,
            patientAge: details.patient_age || details.patientAge,
            phone_no: details.phone_no || details.phone,
            services: servicesList,
            isEmergency: details.isEmergency || details.is_emergency || 0,
            isOutsideOPDEmergency:
              details.isOutsideOPDEmergency ||
              details.is_outside_opd_emergency ||
              0,
          };
          setAppointmentData(formattedData);
          setOriginalData(formattedData);
          setServices(servicesList);
          setIsEmergency(formattedData.isEmergency || 0);
          if (details.doctor_id) {
            await fetchDoctorDetails(details.doctor_id, false);
            if (details.appointment_date) {
              await fetchTimeSlots(details.doctor_id, details.appointment_date);
            }
          }
          await fetchExistingBill(details.id || appointment.id);
        }
      } catch (error) {
        console.error("Error loading appointment", error);
        toast({
          title: "Error",
          description:
            error.response?.data?.message ||
            "Failed to fetch appointment details.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadAppointmentDetails();
  }, [
    isOpen,
    appointment?.id,
    // fetchDoctorDetails,
    // fetchTimeSlots,
    // fetchExistingBill,
    // toast
  ]);

  useEffect(() => {
    if (!appointmentData) return;
    calculateBillSummary();
  }, [
    services,
    isEmergency,
    appointmentData?.isOutsideOPDEmergency,
    selectedDoctorDetails,
    calculateBillSummary,
  ]);

  const handleInputChange = async (event) => {
    const { name, value } = event.target;
    setAppointmentData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "doctorId" && value) {
      await fetchDoctorDetails(value, true);
      if (appointmentData?.appointmentDate) {
        await fetchTimeSlots(value, appointmentData.appointmentDate);
      }
    }
    if (name === "appointmentDate" && value) {
      if (appointmentData?.doctorId) {
        await fetchTimeSlots(appointmentData.doctorId, value);
      }
    }
    if (name === "appointmentTime" && value) {
      calculateBillSummary();
    }
  };

  const handleServiceSelection = (serviceName) => {
    const selectedService = doctorServices.find(
      (service) => service.name === serviceName,
    );
    if (selectedService) {
      setServiceDetails({
        serviceId: serviceName,
        unitPrice: selectedService.unitPrice || "",
        discount: selectedService.discount || "",
      });
    } else {
      setServiceDetails({
        serviceId: serviceName,
        unitPrice: "",
        discount: "",
      });
    }
  };

  const addService = () => {
    if (
      serviceDetails.serviceId &&
      serviceDetails.unitPrice !== "" &&
      serviceDetails.discount !== ""
    ) {
      const isServicePresent = services.some(
        (service) => service.serviceName === serviceDetails.serviceId,
      );
      if (isServicePresent) {
        toast({
          title: "Duplicate Service",
          description: "This service is already added.",
          status: "warning",
          duration: 2500,
          isClosable: true,
        });
        return;
      }
      setServices((prev) => [
        ...prev,
        {
          serviceName: serviceDetails.serviceId,
          unitPrice: serviceDetails.unitPrice,
          discount: serviceDetails.discount,
        },
      ]);
      setServiceDetails({ serviceId: "", unitPrice: "", discount: "" });
    } else {
      toast({
        title: "Missing Information",
        description: "Select a service and provide unit price and discount.",
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  const removeService = (index) => {
    // in edit do not let user to remove all the services
    // ignore Emergency Charge and OutSide OPD Charge
    if (services[index].serviceName === "OutSide OPD Charge") {
      toast({
        title: "Removing OutSide OPD Charge",
        description: " Outside OPD charge is removed.",
        status: "info",
        duration: 2500,
        isClosable: true,
      });
      setServices((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    if (services[index].serviceName === "Emergency Charge") {
      toast({
        title: "Removing Emergency Charge",
        description: "Emergency Charge is removed.",
        status: "info",
        duration: 2500,
        isClosable: true,
      });
      setServices((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    if (
      services[index].serviceName !== "Emergency Charge" ||
      services[index].serviceName !== "OutSide OPD Charge"
    ) {
      if (services.length < 2) {
        toast({
          title: "Cannot Remove Service",
          description: "At least one service is required.",
          status: "error",
          duration: 2500,
          isClosable: true,
        });
        return;
      }
      setServices((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleEmergencyChange = (event) => {
    const value = Number(event.target.value);
    setIsEmergency(value);
    setAppointmentData((prev) => ({
      ...prev,
      isEmergency: value,
    }));
  };

  const handleOutsideEmergencyChange = (event) => {
    const value = Number(event.target.value);
    setAppointmentData((prev) => ({
      ...prev,
      isOutsideOPDEmergency: value,
    }));
  };

  // Received amount is now read-only, no need for change handler

  const handleReceiptFileChange = (event) => {
    const files = ensureArray(event.target.files);
    setReceiptFiles(files);
    setReceiptError("");
  };

  const handleBillModalClose = () => {
    setBillSummaryVisible(false);
    setIsBillProcessing(false);
    setReceiptFiles([]);
    setReceiptError("");
  };

  const syncBillWithBackend = useCallback(async () => {
    if (!appointmentData) {
      throw new Error("Appointment data unavailable for billing.");
    }

    if (
      !existingBillData ||
      !(existingBillData.id || existingBillData.bill_id)
    ) {
      throw new Error("No existing bill found to update.");
    }

    const billId = existingBillData.bill_id || existingBillData.id;
    const appointmentId =
      appointmentData.id || appointmentData.appointment_id || appointment?.id;

    if (!billId || !appointmentId) {
      throw new Error("Missing identifiers required for bill update.");
    }

    const netAmount = normalizeNumber(billSummary.netAmount);
    const receivedAmount = normalizeNumber(billSummary.RecivedAmt);
    // Calculate pending and refund amounts
    const rawPending = Number(
      (
        normalizeNumber(billSummary.netAmount) -
        normalizeNumber(billSummary.RecivedAmt)
      ).toFixed(2),
    );
    // For overpayment scenario: if existing received > current total, set pending as negative (-30)
    const pendingAmount = rawPending; // can be negative for overpayment
    const refundAmount = rawPending < 0 ? Math.abs(rawPending) : 0;

    setReceiptError("");

    let invoiceUrl =
      existingBillData.bill_invoice ||
      existingBillData.billInvoice ||
      existingBillData.bill_invoice_url ||
      "";

    try {
      // Use shared helper to render the Bill UI offscreen and upload the PDF
      const uploadedInvoice = await renderAndUploadBill({
        appointmentData,
        selectedServices: computedServices,
        billSummary,
        paymentState: pendingAmount < 0 ? "REFUND" : "PAID",
        paidAmount: Number(billSummary.RecivedAmt) || 0,
        pendingAmount: Number(pendingAmount) || 0,
        refundAmount: Number(refundAmount) || 0,
        fileName: `updated_bill_${appointmentData.patientName || "patient"}.pdf`,
      });
      if (uploadedInvoice) {
        invoiceUrl = uploadedInvoice;
      }
    } catch (error) {
      console.warn("Failed to generate or upload invoice", error);
    }

    const receiptUrls = [];
    if (receiptFiles && receiptFiles.length) {
      for (const [index, file] of receiptFiles.entries()) {
        try {
          const nameParts = (file?.name || "receipt").split(".");
          const ext = nameParts.length > 1 ? nameParts.pop() : "pdf";
          const uploadedReceipt = await uploadFile(file, {
            fileName: `payment_receipt_${billId}_${Date.now()}_${index + 1}.${
              ext || "pdf"
            }`,
          });
          if (uploadedReceipt) {
            receiptUrls.push(uploadedReceipt);
          }
        } catch (error) {
          console.warn("Failed to upload receipt", error);
          setReceiptError("Failed to upload one or more receipt files.");
        }
      }
    }

    const serviceNames =
      computedServices
        .map(
          (service) =>
            service.serviceName ||
            service.name ||
            service.service_name ||
            "Service",
        )
        .join(",") ||
      existingBillData.service ||
      "";

    const unitPrices =
      computedServices
        .map((service) => Number(normalizeNumber(service.unitPrice)).toFixed(2))
        .join(",") ||
      existingBillData.unit_price ||
      "";

    const discounts =
      computedServices
        .map((service) => Number(normalizeNumber(service.discount)).toFixed(2))
        .join(",") ||
      existingBillData.discount ||
      "";

    const appointmentDate =
      appointmentData.appointmentDate ||
      existingBillData.appointment_date ||
      existingBillData.appointmentDate;

    const appointmentTime = appointmentData.appointmentTime
      ? convertTo24HourFormat(appointmentData.appointmentTime)
      : existingBillData.appointment_time || existingBillData.appointmentTime;

    const stringifyAmount = (value) =>
      Number(normalizeNumber(value)).toFixed(2);

    const existingReceiptField =
      existingBillData.payment_receipt_urls ||
      existingBillData.payment_receipt_url ||
      existingBillData.payment_receipt ||
      null;

    const paymentReceiptUrlsValue = receiptUrls.length
      ? receiptUrls.length === 1
        ? receiptUrls[0]
        : receiptUrls.join(",")
      : existingReceiptField;

    const paymentReceiptValue = receiptUrls.length
      ? receiptUrls[0]
      : existingBillData.payment_receipt || null;

    const updatePayload = {
      ...existingBillData,
      id: existingBillData.id || billId,
      bill_id: existingBillData.bill_id || billId,
      appointment_id: appointmentId,
      appointment_date: appointmentDate,
      appointment_time: appointmentTime,
      consultation_type:
        appointmentData.appointmentType ||
        existingBillData.consultation_type ||
        existingBillData.consultationType,
      service: serviceNames,
      unit_price: unitPrices,
      discount: discounts,
      total_amt: stringifyAmount(netAmount),
      received_amt: stringifyAmount(receivedAmount),
      pending_amt: stringifyAmount(pendingAmount), // negative for overpayment (-30)
      refund_amt: stringifyAmount(refundAmount),
      bill_description:
        billDescription ?? existingBillData.bill_description ?? "",
      payment_action: paymentState,
      payment_status: paymentState,
      invoice_status: invoiceStatus,
      bill_invoice: invoiceUrl || existingBillData.bill_invoice || "",
      payment_receipt_urls: paymentReceiptUrlsValue,
      payment_receipt: paymentReceiptValue,
      tax_amount: stringifyAmount(billSummary.taxAmount || 0),
      tax_percentage: Number(billSummary.taxPercentage ?? billSummary.tax_percent ?? existingBillData?.tax_percentage ?? existingBillData?.taxPercentage ?? selectedDoctorDetails?.doctor?.tax_percentage ?? selectedDoctorDetails?.doctor?.taxPercentage ?? 10),
    };

    Object.keys(updatePayload).forEach((key) => {
      if (updatePayload[key] === undefined) {
        delete updatePayload[key];
      }
    });

    const authToken =
      localStorage.getItem("token") || localStorage.getItem("Token") || "";

    await axios.put(BILLING_UPDATE_URL(billId), updatePayload, {
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      maxBodyLength: Infinity,
    });

    try {
      await axiosInstance.post("/api/appointment/updatePaymentStatus", {
        appointment_id: appointmentId,
        payment_action: paymentState,
        paid_amount: normalizeNumber(updatePayload.received_amt),
        pending_amount: normalizeNumber(updatePayload.pending_amt),
        refund_amount: normalizeNumber(updatePayload.refund_amt),
      });
    } catch (error) {
      console.warn("Failed to update payment status", error);
    }

    setExistingBillData((prev) => ({
      ...prev,
      ...updatePayload,
    }));
  }, [
    appointment,
    appointmentData,
    billDescription,
    billSummary,
    computedServices,
    existingBillData,
    invoiceStatus,
    paymentState,
    receiptFiles,
  ]);

  const handleBillSync = async () => {
    setIsBillProcessing(true);
    try {
      await syncBillWithBackend();
      toast({
        title: "Bill Updated",
        description: "Bill summary synced successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setBillSummaryVisible(false);
      setReceiptFiles([]);
      setReceiptError("");
      if (pendingUpdatePayload) {
        onUpdate && onUpdate(pendingUpdatePayload);
      }
      onClose();
    } catch (error) {
      console.error("Failed to sync bill", error);
      toast({
        title: "Billing Error",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to sync bill. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsBillProcessing(false);
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!appointmentData) return;
    setIsSubmitting(true);
    onConfirmClose();
    try {
      let appointmentTimeForAPI = appointmentData.appointmentTime;
      if (
        appointmentData.appointmentTime &&
        appointmentData.appointmentTime.includes(" ")
      ) {
        appointmentTimeForAPI = convertTo24HourFormat(
          appointmentData.appointmentTime,
        );
      } else if (
        appointmentData.appointmentTime &&
        !appointmentData.appointmentTime.includes(" ")
      ) {
        const selectedSlot = availableTimeSlots.find(
          (slot) =>
            slot.display === appointmentData.appointmentTime ||
            slot.value === appointmentData.appointmentTime,
        );
        appointmentTimeForAPI = selectedSlot
          ? `${selectedSlot.value}:00`
          : `${appointmentData.appointmentTime}:00`;
      }

      const updatedServices = services.map((service) => ({
        serviceName: service.serviceName,
        unitPrice: Number(service.unitPrice),
        discount: Number(service.discount),
      }));

      const payload = {
        appointment_id: originalData?.id || originalData?.appointment_id,
        appointment_date: appointmentData.appointmentDate,
        appointment_time: appointmentTimeForAPI,
        appointment_type: appointmentData.appointmentType,
        services: updatedServices,
        isEmergency,
        isOutsideOPDEmergency:
          Number(appointmentData.isOutsideOPDEmergency) || 0,
        doctor_id: appointmentData.doctorId,
      };

      await axiosInstance.put("api/appointment/update", payload);

      if (isEmergency === 1) {
        try {
          await axiosInstance.post(
            `api/appointment/markAsEmergency/${payload.appointment_id}`,
          );
        } catch (error) {
          console.warn("Failed to mark emergency", error);
        }
      }

      toast({
        title: "Appointment Updated",
        description: "Review the updated bill summary before closing.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      setPendingUpdatePayload({
        ...appointmentData,
        ...payload,
      });

      await fetchExistingBill(payload.appointment_id);
      calculateBillSummary();
      setBillSummaryVisible(true);
    } catch (error) {
      console.error("Error updating appointment", error);
      toast({
        title: "Update Failed",
        description:
          error.response?.data?.message ||
          "Failed to update appointment. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateClick = () => {
    onConfirmOpen();
  };

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Loading Appointment Details...</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Loading appointment details, please wait...</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (!appointmentData) {
    return null;
  }

  return (
    <>
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Update
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to update the appointment for
              <Text as="span" fontWeight="semibold" color="blue.500">
                {" "}
                {appointmentData.patientName || "this patient"}
              </Text>
              ?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleUpdate}
                ml={3}
                isLoading={isSubmitting}
              >
                Update Appointment
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl">
        <ModalOverlay />
        <ModalContent p={6}>
          <ModalHeader>Update Appointment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex alignItems="center" mb={6} gap={6}>
              <Avatar size="xl" src={""} />
              <Box>
                <Text fontWeight="bold" fontSize="2xl">
                  {appointmentData.patientName}
                  <Text
                    as="span"
                    fontWeight="normal"
                    fontSize="md"
                    color="gray.500"
                    ml={2}
                  >
                    ({appointmentData.patientId})
                  </Text>
                </Text>
                <Text color="gray.600">
                  {appointmentData.patientGender}, {appointmentData.patientAge}{" "}
                  years old
                </Text>
                <Text color="gray.600">
                  <strong>Phone:</strong> {appointmentData.phone_no}
                </Text>
              </Box>
            </Flex>

            <Flex mb={6} gap={4} wrap="wrap">
              <Box flex="1" minW="200px">
                <FormLabel>Doctor</FormLabel>
                <Select
                  name="doctorId"
                  placeholder="Select Doctor"
                  value={appointmentData.doctorId}
                  onChange={handleInputChange}
                >
                  {doctors.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.doctor}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box flex="1" minW="200px">
                <FormLabel>Appointment Date</FormLabel>
                <Input
                  // type="date"
                  name="appointmentDate"
                  value={appointmentData.appointmentDate}
                  onChange={handleInputChange}
                  min={getMinDate()}
                />
              </Box>
              <Box flex="1" minW="200px">
                <FormLabel>Appointment Time</FormLabel>
                <Select
                  name="appointmentTime"
                  // isDisabled={!availableTimeSlots.length}
                  placeholder={
                    isLoadingTimeSlots ? "Loading time slots..." : "Select Time"
                  }
                  value={appointmentData.appointmentTime || ""}
                  onChange={handleInputChange}
                  isDisabled={
                    !appointmentData.doctorId ||
                    !appointmentData.appointmentDate ||
                    isLoadingTimeSlots
                  }
                >
                  {availableTimeSlots.map((slot, index) => (
                    <option
                      key={index}
                      value={slot.value}
                      disabled={slot.isPast}
                    >
                      {slot.display}
                      {slot.isPast ? " (Past)" : ""}
                    </option>
                  ))}
                </Select>
              </Box>
            </Flex>

            <Flex mb={6} gap={4} wrap="wrap">
              <Box flex="1" minW="200px">
                <FormLabel>Appointment Type</FormLabel>
                <Select
                  name="appointmentType"
                  value={appointmentData.appointmentType}
                  onChange={handleInputChange}
                >
                  {appointmentTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box flex="1" minW="200px">
                <FormLabel>Is Emergency</FormLabel>
                <Select value={isEmergency} onChange={handleEmergencyChange}>
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </Select>
              </Box>
              <Box flex="1" minW="200px">
                <FormLabel>Outside OPD Emergency</FormLabel>
                <Select
                  value={Number(appointmentData.isOutsideOPDEmergency) || 0}
                  onChange={handleOutsideEmergencyChange}
                >
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </Select>
              </Box>
            </Flex>

            <Flex mb={6} gap={4} wrap="wrap">
              <Box flex="1" minW="200px">
                <FormLabel>Service</FormLabel>
                <Select
                  placeholder={
                    isLoadingServices ? "Loading services..." : "Select Service"
                  }
                  value={serviceDetails.serviceId}
                  onChange={(event) =>
                    handleServiceSelection(event.target.value)
                  }
                  isDisabled={
                    !appointmentData.doctorId ||
                    doctorServices.length === 0 ||
                    isLoadingServices
                  }
                >
                  {doctorServices.map((service) => (
                    <option key={service.id} value={service.name}>
                      {service.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box flex="1" minW="200px">
                <FormLabel>Unit Price</FormLabel>
                <Input
                  type="number"
                  min="0"
                  name="unitPrice"
                  value={serviceDetails.unitPrice}
                  onChange={(event) =>
                    setServiceDetails((prev) => ({
                      ...prev,
                      unitPrice: event.target.value,
                    }))
                  }
                />
              </Box>
              <Box flex="1" minW="200px">
                <FormLabel>Discount (%)</FormLabel>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  name="discount"
                  value={serviceDetails.discount}
                  onChange={(event) =>
                    setServiceDetails((prev) => ({
                      ...prev,
                      discount: event.target.value,
                    }))
                  }
                />
              </Box>
              <Flex alignItems="flex-end">
                <Button colorScheme="blue" onClick={addService}>
                  Add Service
                </Button>
              </Flex>
            </Flex>

            {services.length > 0 && (
              <Box mb={6}>
                <FormLabel>Selected Services</FormLabel>
                <Stack spacing={3}>
                  {services.map((service, index) => (
                    <Flex
                      key={`${service.serviceName}-${index}`}
                      alignItems="center"
                      justifyContent="space-between"
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                    >
                      <Box>
                        <Text fontWeight="semibold">
                          {service.serviceName || "Service"}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          ₹{service.unitPrice} · Discount {service.discount}%
                        </Text>
                      </Box>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        onClick={() => removeService(index)}
                      >
                        Remove
                      </Button>
                    </Flex>
                  ))}
                </Stack>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleUpdateClick}
              isLoading={isSubmitting}
            >
              Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={billSummaryVisible}
        onClose={handleBillModalClose}
        size="5xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent p={6} maxH="90vh" overflowY="auto">
          <ModalHeader>Updated Bill Summary</ModalHeader>
          <ModalCloseButton isDisabled={isBillProcessing} />
          <ModalBody>
            <Flex align="center" justify="space-between" mb={4}>
              <Text fontWeight="semibold">Payment Status</Text>
              <Badge
                colorScheme={paymentBadgeScheme}
                fontSize="0.9em"
                px={3}
                py={1}
                borderRadius="md"
              >
                {paymentState}
              </Badge>
            </Flex>
            <Bill
              appointmentData={appointmentData}
              selectedServices={computedServices}
              billSummary={billSummary}
              paymentState={paymentState}
              billPaymentNowSelection={billPaymentNowSelection}
              pendingAmount={billSummary.pendingAmount}
              refundAmount={billSummary.refundAmount}
              paidAmount={billSummary.RecivedAmt}
            />
            <Divider my={6} />
            <Stack spacing={4}>
              <Flex gap={4} wrap="wrap">
                <Box flex="1" minW="220px">
                  <FormLabel>Amount Received (₹)</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={billSummary.RecivedAmt}
                    // isReadOnly
                    onChange={(e) => {
                      if (e.target.value < billSummary.netAmount) {
                        setBillSummary((prev) => ({
                          ...prev,
                          RecivedAmt: e.target.value,
                          pendingAmount: prev.netAmount - e.target.value,
                          refundAmount: prev.netAmount - e.target.value,
                        }));
                      } else {
                        setBillSummary((prev) => ({
                          ...prev,
                          RecivedAmt: prev.netAmount,
                          pendingAmount: 0,
                          refundAmount: 0,
                        }));
                      }
                    }}
                  />
                </Box>
                {billSummary.refundAmount > 0 ? (
                  <Box flex="1" minW="220px">
                    <FormLabel>Refund Amount (₹)</FormLabel>
                    <Input value={billSummary.refundAmount} isReadOnly />
                  </Box>
                ) : (
                  <Box flex="1" minW="220px">
                    <FormLabel>Pending Amount (₹)</FormLabel>
                    <Input value={billSummary.pendingAmount} isReadOnly />
                  </Box>
                )}
              </Flex>
              <Box>
                <FormLabel>Invoice Notes</FormLabel>
                <Textarea
                  value={billDescription}
                  onChange={(event) => setBillDescription(event.target.value)}
                  placeholder="Add notes that will appear on the invoice"
                  isDisabled={isBillProcessing}
                />
              </Box>
              <Box>
                <FormLabel>Upload Payment Receipts (optional)</FormLabel>
                <FileUploadField
                  id="payment-receipts"
                  name="payment_receipts"
                  onChange={handleReceiptFileChange}
                  accept=".pdf,image/*"
                  inputProps={{ multiple: true }}
                  isDisabled={isBillProcessing}
                  helperText="You can select multiple files"
                />
                {receiptError && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {receiptError}
                  </Text>
                )}
              </Box>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={handleBillModalClose}
              isDisabled={isBillProcessing}
            >
              Close
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleBillSync}
              isLoading={isBillProcessing}
            >
              Save Bill Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditAppointmentModal;
